<template>
  <transition name="modal">
    <div class="modal-mask" @click="$emit('close')">
      <div class="modal">
        <div class="modal-dialog">
          <div class="modal-content" @click.stop>
            <div v-if="notification.name === 'Notification'">
              <div class="modal-header">
                <h5 class="modal-title" v-translate>Select notification type</h5>
                <button type="button" class="close" @click="$emit('close')">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="modal-body">
                <div>
                  <button
                    class="btn btn-primary btn-orange"
                    id="delay_notification"
                    @click="createDelayNotification"
                  >
                    +
                    <translate>delay notification</translate>
                  </button>
                </div>
                <div>
                  <button
                    class="btn btn-primary btn-orange"
                    id="step_done_notification"
                    @click="createStepDoneNotification"
                  >
                    +
                    <translate>step completion notification</translate>
                  </button>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="$emit('close')">
                  Close
                </button>
              </div>
            </div>
            <form v-else v-on:submit.prevent="saveFilter">
              <div class="notification-configurator">
                <div class="modal-header">
                  <h5 class="modal-title">
                    <translate>Configure notification for</translate>
                    {{ notification.name }}
                  </h5>
                  <button type="button" class="close" @click="$emit('close')">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div class="modal-body">
                  <div class="form-inline" v-if="notification.name === 'StepDelay'">
                    <div class="form-group">
                      <translate>If the</translate>
                      <select
                        v-model="notification.attachedStepId"
                        class="form-control"
                        id="delay-step"
                        required
                      >
                        <option
                          v-for="option in stepOptions"
                          v-bind:value="option.value"
                          :key="option.value"
                        >
                          {{ option.text }}
                        </option>
                      </select>
                    </div>
                    <div class="form-group">
                      <translate>is delayed by more than</translate>
                      <input
                        type="number"
                        v-model="notification.delayThresholdMinutes"
                        placeholder="15"
                        min="1"
                        max="90"
                        name="delay-input-minutes"
                        class="form-control"
                        required
                      />
                      <translate>minutes,</translate>
                    </div>
                    <div>
                      <translate>inform me via</translate>
                      <select
                        v-model="notification.channelName"
                        id="delay-channel"
                        class="form-control"
                        required
                      >
                        <option
                          v-for="(friendlyName, channelName) in channelOptions"
                          v-bind:value="channelName"
                          :key="friendlyName"
                        >
                          {{ friendlyName }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="form-inline" v-else-if="notification.name === 'StepDone'">
                    <div class="form-group">
                      <translate>If the</translate>
                      <select
                        v-model="notification.attachedStepId"
                        id="done-step"
                        class="form-control"
                        required
                      >
                        <option
                          v-for="option in stepOptions"
                          v-bind:value="option.value"
                          :key="option.value"
                        >
                          {{ option.text }}
                        </option>
                      </select>
                      <translate>is done</translate>
                    </div>
                    <div>
                      <translate>inform me via</translate>
                      <select
                        v-model="notification.channelName"
                        class="form-control"
                        id="done-channel"
                        required
                      >
                        <option
                          v-for="(friendlyName, channelName) in channelOptions"
                          v-bind:value="channelName"
                          :key="friendlyName"
                        >
                          {{ friendlyName }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div v-else>
                    {{ notification.name }}
                    No Template found for this configuration
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button
                  type="button"
                  v-show="filter"
                  :disabled="buttonsDisabled"
                  class="modal-default-button btn btn-warning btn-delete mr-auto"
                  @click="deleteFilter"
                >
                  <i class="material-icons">delete</i>
                </button>
                <button
                  type="button"
                  :disabled="buttonsDisabled"
                  class="modal-default-button btn btn-default"
                  @click="$emit('close')"
                  v-translate
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  :disabled="buttonsDisabled"
                  class="modal-default-button btn btn-primary btn-save"
                  v-translate
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import baseController from '../../controllers/base'
import Filter from '../../models/filter'
import User from '../../models/user'
import StepDelayNotification from '../../models/notifications/step-delay-notification'
import StepDoneNotification from '../../models/notifications/step-done-notification'
import Notification from '../../models/notifications/notification'
import {
  createNotification,
  updateNotification,
  deleteNotification
} from '@/models/notifications/queries'

export default {
  extends: baseController,
  props: {
    filter: { type: Object },
    transfer: { type: Object }
  },
  data() {
    return {
      channelOptions: Filter.channelOptions,
      notification: this.filter
        ? this.transfer.buildNotification(this.filter)
        : new Notification(this.transfer),
      buttonsDisabled: false
    }
  },
  computed: {
    stepOptions() {
      return this.transfer.steps.map(({ name, id }) => ({
        text: name,
        value: id
      }))
    }
  },
  created() {
    return document.addEventListener('keydown', this.closeOnEscape)
  },
  destroyed() {
    return document.removeEventListener('keydown', this.closeOnEscape)
  },
  methods: {
    async saveFilter() {
      const updatedFilter = this.notification.buildFilter()
      this.buttonsDisabled = true
      if (updatedFilter.id) {
        await updateNotification(updatedFilter)
        this.$emit('close')
      } else {
        const user = await User.me()
        updatedFilter.recipient = user.id
        await createNotification(updatedFilter)
        this.$emit('close')
      }
    },
    async deleteFilter() {
      this.buttonsDisabled = true
      await deleteNotification(this.notification.filter)
      this.$emit('close')
    },
    createDelayNotification() {
      this.notification = new StepDelayNotification(this.transfer)
    },
    createStepDoneNotification() {
      this.notification = new StepDoneNotification(this.transfer)
    },
    closeOnEscape(event) {
      if (event.keyCode === 27) {
        this.$emit('close')
      }
    }
  }
}
</script>
